import type { SelectItem, TextInputProps } from '@_/ui';
import type { CountryCode } from 'libphonenumber-js/max/es6';
import { getCountries, getCountryCallingCode } from 'libphonenumber-js/max/es6';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import Input from 'react-phone-number-input/input';
import { Badge, FormattedCountry, Select, TextInput, TextInputGroup } from '@_/ui';
import { sortInLocale } from '@_/utils';

const DEFAULT_COUNTRY_CODE = 'HK';

const ALL_COUNTRIES = getCountries();

export interface PhoneInputValue {
  countryCode: string;
  number: string;
}

export interface PhoneInputProps extends Pick<
  TextInputProps,
  'cornerText' | 'disabled' | 'error' | 'helpText' | 'label'
> {
  allowedCountries?: string[];
  onBlur?: () => void;
  onChange?: (value: PhoneInputValue) => void;
  value: PhoneInputValue | null;
}

export function PhoneInput({
  allowedCountries,
  cornerText,
  disabled,
  error,
  helpText,
  label,
  onBlur,
  onChange,
  value
}: PhoneInputProps) {
  const { locale } = useIntl();

  const { countryCode = DEFAULT_COUNTRY_CODE, number = '' } = value || {};

  const handleChangeCountryCode = useCallback(
    (updatedValue: string) => {
      onChange?.({ countryCode: updatedValue, number });
    },
    [number, onChange]
  );

  const handleChangeNumber = useCallback(
    (value: string | undefined) => {
      if (!countryCode) {
        return;
      }

      if (value) {
        const dialCode = getCountryCallingCode(countryCode as CountryCode);
        const prefixRegex = new RegExp(`^\\+${dialCode}`);
        onChange?.({
          countryCode,
          number: prefixRegex.test(value) ? value.slice(dialCode.length + 1) : value
        });
      } else {
        onChange?.({ countryCode, number: '' });
      }
    },
    [countryCode, onChange]
  );

  const countryItems = useMemo<Array<SelectItem<string>>>(() => {
    const countryFormatter = new Intl.DisplayNames(locale, {
      type: 'region',
      style: 'short'
    });

    return sortInLocale(
      locale,
      allowedCountries ? allowedCountries.slice() : ALL_COUNTRIES.slice(),
      code => countryFormatter.of(code) ?? ''
    )
      .filter(code => {
        try {
          getCountryCallingCode(code as CountryCode);
          return true;
        } catch (err) {
          return false;
        }
      })
      .map(code => {
        const dialCode = getCountryCallingCode(code as CountryCode);

        return {
          content: ({ placement }) =>
            placement === 'value' ? (
              `+${dialCode}`
            ) : (
              <div className="flex items-center gap-x-2">
                <div className="w-12 grow-0 text-right">
                  <Badge size="sm">{dialCode}</Badge>
                </div>
                <div className="grow">
                  <FormattedCountry displayStyle="long" value={code} />
                </div>
              </div>
            ),
          value: code
        };
      });
  }, [allowedCountries, locale]);

  const currentDialCode = countryCode ? getCountryCallingCode(countryCode as CountryCode) : '';

  return (
    <TextInputGroup
      cornerText={cornerText}
      disabled={disabled}
      error={error}
      helpText={helpText}
      label={label}
    >
      <Select
        disabled={disabled}
        items={countryItems}
        onChange={handleChangeCountryCode}
        value={countryCode}
      />
      <div className="grow">
        <Input
          country={countryCode as CountryCode}
          disabled={disabled}
          inputComponent={TextInput}
          onBlur={onBlur}
          onChange={handleChangeNumber}
          value={number ? `+${currentDialCode}${number}` : ''}
        />
      </div>
    </TextInputGroup>
  );
}
